/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";

import classNames from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import posthog from "posthog-js";

import { YellowBannerObject } from "data/homepage";
import { ProductData } from "data/product/products";
import { Include } from "data/videoProductPage";
import { getIncludedSectionData } from "data/videoProductPage";
import { Button } from "studio-design-system";

import {
  PRODUCT_LANDING_PAGE_VISIT,
  PRODUCT_PAGE_BUY_NOW_SELECTED,
} from "~/analytics/Amplitude/amplitudeConstants";
import { addUTMPropertiesToEvent } from "~/analytics/Amplitude/amplitudeUtils";
import { useAnalytics } from "~/analytics/useAnalytics";
import { PlayVideoIcon } from "~/components/common/Icons/PlayVideoIcon";
import { ImageWithBlur } from "~/components/common/ImageWithBlur/ImageWithBlur";
import { LandingPageSources, useProductLandingPage } from "~/components/homepage/useLandingPage";
import { Divider } from "~/components/videoProductPage/common/Divider/Divider";
import { VideoPlayer } from "~/components/videoProductPage/common/VideoPlayer/VideoPlayer";
import usePriceCountdown from "~/components/videoProductPage/common/usePriceCountdown";
import * as styles from "~/components/videoProductPage/components/ProductBuy/ProductBuy.css";
import { useAuth } from "~/hooks/auth/useAuth";
import { usePriceOfferInfoCheckout } from "~/hooks/usePriceOfferInfo";
import { getResponsiveImageURL } from "~/util/cloudinary-utils";
import { formatMoney } from "~/util/stringUtils";

import { AddToCartButton } from "../AddToCartButton/AddToCartButton";

export const ProductBuy = ({
  product,
  handleShowModal,
  handleAddToCartShowModal,
  hasVideoURL,
  isInEditMode,
}: {
  product: ProductData;
  handleShowModal: () => void;
  handleAddToCartShowModal: () => void;
  hasVideoURL: boolean;
  isInEditMode: boolean;
}): React.ReactElement => {
  const { trackAmplitudeEvent } = useAnalytics();
  const { expired, days, hours, minutes, seconds } = usePriceCountdown({
    targetDate: product.priceInfo.crossedPriceApplicableFrom,
  });
  const includeSectionData = getIncludedSectionData(product);
  const { onCtaClick } = useProductLandingPage(LandingPageSources.Product, product);
  const { shouldUserSeeOfferCalculation } = usePriceOfferInfoCheckout();
  const [showPriceOffer, setShowPriceOffer] = useState(false);
  const [offerTheme, setOfferTheme] = useState("none");
  const [offerEndsText, setOfferEndsText] = useState("Offer ends in");
  const { user, loadingCurrentUser } = useAuth();
  const router = useRouter();
  const { p } = router.query;
  const [isBuyNowPrioritized, setIsBuyNowPrioritized] = useState(false);

  const handleBuyNow = () => {
    trackAmplitudeEvent(PRODUCT_PAGE_BUY_NOW_SELECTED, {
      ...addUTMPropertiesToEvent(),
    });
    onCtaClick();
  };

  useEffect(() => {



    const shouldUserSeeOffer = shouldUserSeeOfferCalculation(product.priceInfo);
    //const shouldUserSeeOffer = true;


    setShowPriceOffer(shouldUserSeeOffer);

    // posthog.feature_flags.override({ "flip-purchase-button": "buy-now-priority" });
    posthog.onFeatureFlags(function () {
      if (posthog.isFeatureEnabled("homepage-yellow-banner-text")) {
        const yellowBannerInfo = posthog.getFeatureFlagPayload(
          "homepage-yellow-banner-text",
        ) as unknown as YellowBannerObject;
        if (yellowBannerInfo) {
          setOfferTheme(yellowBannerInfo.specialTheme);

          if (yellowBannerInfo.specialOfferTitle) {
            setOfferEndsText(yellowBannerInfo.specialOfferTitle + " offer ends in");
          }
        }
      }
    });

    if (posthog.getFeatureFlag("flip-purchase-button") === "buy-now-priority") {
      // Do something differently for this user
      setIsBuyNowPrioritized(true);

    } else {
      // It's a good idea to let control variant always be the default behaviour,
      // so if something goes wrong with flag evaluation, you don't break your app.
      setIsBuyNowPrioritized(false);
    }
  }, [user, loadingCurrentUser, p]);

  return (
    <div className={styles.productBuy}>
      <div className={styles.productBuyInner}>
        {product.productImageURL && hasVideoURL && (
          <div
            onClick={() => {
              hasVideoURL && handleShowModal();
            }}
            className={
              hasVideoURL
                ? classNames(styles.videoPlayerOverlay, styles.pointerCursor)
                : styles.videoPlayerOverlay
            }
          ></div>
        )}
        <div className={styles.productBuyPreviewImage}>
          {isInEditMode && (!product.productImageURL || !hasVideoURL) ? (
            <div className={classNames(styles.emptyPreviewImageContainer)}>
              <span>
                Upload your class trailer and class poster on the{" "}
                <span
                  className={styles.linkStyle}
                  onClick={() => router.push("/classes/create/" + product.id)}
                >
                  <Link href={"/classes/create/" + product.id}>Class Details page</Link>
                </span>
              </span>
            </div>
          ) : (
            <VideoPlayer
              url="https://"
              light={getResponsiveImageURL(product.productImageURL, "500") || ""}
              height={235}
              playIcon={
                <div className={styles.previewImageContainer}>
                  {hasVideoURL && (
                    <>
                      <PlayVideoIcon height={45} width={45} />
                      <p className={styles.previewImageText}>Watch Trailer</p>
                    </>
                  )}
                </div>
              }
            />
          )}
        </div>
        <div className={styles.productBuyInfo}>
          <div className={styles.productBuyPrice}>
            <span
              className={classNames(
                styles.productBuyNewPrice,
                !showPriceOffer && !isInEditMode && styles.expired,
              )}
            >
              {showPriceOffer || isInEditMode
                ? formatMoney(product.priceInfo.price)
                : formatMoney(product.priceInfo.crossedPrice)}
            </span>
            {showPriceOffer || isInEditMode ? (
              <span className={styles.productBuyOldPrice}>
                {formatMoney(product.priceInfo.crossedPrice)}
              </span>
            ) : (
              <></>
            )}
          </div>
          <div className={styles.buttonContainer}>
            {isInEditMode ? (
              <Button
                variant={"secondary"}
                onClick={handleBuyNow}
                disabled={isInEditMode}
                className={classNames(styles.ctaButton, styles.black)}
              >
                Add price on Class Details page
              </Button>
            ) : (
              <>
                {isBuyNowPrioritized ? (
                  <>
                    <Button
                      onClick={handleBuyNow}
                      disabled={isInEditMode}
                      className={classNames(styles.ctaButton)}
                    >
                      Buy now
                    </Button>
                    <AddToCartButton
                      variant={"secondary"}
                      product={product}
                      disabled={isInEditMode}
                      handleAddToCartShowModal={handleAddToCartShowModal}
                    />
                  </>
                ) : (
                  <>
                    <AddToCartButton
                      product={product}
                      disabled={isInEditMode}
                      handleAddToCartShowModal={handleAddToCartShowModal}
                    />
                    <Button
                      variant={"secondary"}
                      onClick={handleBuyNow}
                      disabled={isInEditMode}
                      className={classNames(styles.ctaButton, styles.black)}
                    >
                      Buy now
                    </Button>
                  </>
                )}
              </>
            )}
          </div>

          {!isInEditMode && (
            <div className={styles.offerInfo}>
              {showPriceOffer && !expired && (
                <div
                  className={classNames(
                    styles.offerBlock,
                    offerTheme !== "none" && styles.springTheme,
                    offerTheme === "holiday" && styles.holidayTheme,
                  )}
                >
                  <span className={styles.offerInfoText}>
                    {offerEndsText}
                  </span>
                  <span className={styles.counter}>
                    {days ? ` ${days}d` : "0d"}
                    {hours ? ` ${hours}h` : " 0h"}
                    {minutes ? ` ${minutes}m` : " 0m"}
                    {seconds ? ` ${seconds}s` : " 0s"}
                  </span>
                </div>
              )}
            </div>
          )}

          <Divider marginTop={11} marginBottom={29} />
          <div>
            <p className={styles.includedTitle}>What’s included:</p>
            <ul>
              {includeSectionData.map((includeItem: Include) => (
                <li key={includeItem.text} className={styles.includedItem}>
                  <div className={styles.imageContainer}>
                    <ImageWithBlur
                      className={styles.includedItemImage}
                      src={includeItem.iconUrl}
                      layout={"fill"}
                      alt="include icon"
                    />
                  </div>
                  <p className={styles.includedItemText}>{includeItem.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
